import { useMemo, useEffect } from "react";
import { Form, Input, Select, Button, Checkbox, Spin, notification } from "antd";
import { DeleteOutlined, HomeOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import ImagePicker from "../../components/form/ImagePicker";
import "./styles.scss";
import { useGetBranchesQuery } from "../../services/branches";
import { useGetRolesByLevelQuery } from "../../services/role";
import PageBreadcrumb from "../../components/breadcrumb";
import { AllTrainersRoute, Home } from "../../routes/routepath";
import { useGetTrainersDetailQuery, useUpdateTrainerMutation } from "../../services/trainer";
import { useNavigate, useParams } from "react-router-dom";

/* ─── Constants ──────────────────────────────────────────────────────── */

const TYPE_OPTIONS = [
  { label: 'Personal Training', value: 'Personal Training' },
  { label: 'Pilates',           value: 'Pilates'           },
  { label: 'Therapy',           value: 'Therapy'           },
  { label: 'EMS',               value: 'EMS'               },
  { label: 'Paid Locker',       value: 'Paid Locker'       },
  { label: 'MMA',               value: 'MMA'               },
];

const ID_TYPE_OPTIONS = [
  { label: 'Aadhar',          value: 'Aadhar'          },
  { label: 'Driving Licence', value: 'Driving Licence' },
  { label: 'Passport',        value: 'Passport'        },
  { label: 'Voter ID',        value: 'Voter ID'        },
];

const normalizeIdType = (val?: string): string | undefined => {
  if (!val) return undefined;
  const map: Record<string, string> = {
    'aadhar':          'Aadhar',
    'driving_license': 'Driving Licence',
    'passport':        'Passport',
    'voter_id':        'Voter ID',
  };
  return map[val.toLowerCase()] ?? val;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const EditTrainer = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form]   = Form.useForm();

  const { data: branchesData }                                                    = useGetBranchesQuery(undefined);
  const { data: rolesData }                                                        = useGetRolesByLevelQuery(undefined);
  const { data: trainerData, isLoading: isLoadingTrainer, error: trainerError }   = useGetTrainersDetailQuery(id as any);
  const [updateTrainer, { isLoading: updating }]                                  = useUpdateTrainerMutation();

  const branchOptions = useMemo(() => {
    const list: any[] = (branchesData as any)?.data;
    if (!Array.isArray(list)) return [];
    return list.map((b: any) => ({
      label: `${b.name}${b.branchArea ? ` — ${b.branchArea}` : ''}`,
      value: b.branchId ?? b._id,
    }));
  }, [branchesData]);

  const rolesOptions = useMemo(() => {
    const list: any[] = (rolesData as any)?.data;
    if (!Array.isArray(list) || !list.length) return [];
    return list.map((r: any) => ({ label: r.name, value: r._id }));
  }, [rolesData]);

  useEffect(() => {
    const td: any = trainerData;
    if (!td?.data) return;
    const data: any = td.data;
    const user: any = data.user;

    form.setFieldsValue({
      coachName:         user.name,
      trainerEmail:      user.email,
      coachPhoneNumber:  user.phoneNumber,
      yearsOfExperience: data.experience,
      type:              data.specialization || [],
      role:              user.roleId?._id,
      branchId:          user.branchIds?.length
        ? user.branchIds.map((b: any) => b.branchId || b._id)
        : [],
      photo:          data.photo,
      idType:         normalizeIdType(data.idType),
      idNumber:       data.idNumber,
      passportNumber: data.passportNumber,
      nationality:    data.nationality,
      idFront:        data.idFront,
      idBack:         data.idBack,
      certificates:   data.certifications
        ? data.certifications.map((cert: string) => ({ title: cert }))
        : [],
    });
  }, [trainerData, form]);

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const handleUpdateTrainer = async (values: Record<string, any>) => {
    const payload: Record<string, any> = {
      name:           values.coachName,
      email:          values.trainerEmail,
      phoneNumber:    values.coachPhoneNumber,
      experience:     values.yearsOfExperience,
      specialization: values.type || [],
      roleId:         values.role || null,
      branchIds:      Array.isArray(values.branchId)
        ? values.branchId
        : values.branchId ? [values.branchId] : [],
      photo:          values.photo,
      employeeType:   'coach',
      userType:       'coach',
      idType:         values.idType,
      idNumber:       values.idNumber,
      idFront:        values.idFront,
      idBack:         values.idBack,
      certificate:    values.certificates
        ? values.certificates.map((cert: any) => cert.title)
        : [],
    };

    if (values.passportNumber) payload.passportNumber = values.passportNumber;
    if (values.nationality)    payload.nationality    = values.nationality;

    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined || payload[k] === null || payload[k] === '') delete payload[k];
    });

    try {
      const res = await (updateTrainer as any)({ id, body: payload }).unwrap();
      if (res?.success) navigate(AllTrainersRoute);
    } catch (err) {
      console.error('Update trainer failed', err);
    }
  };

  if (isLoadingTrainer) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Loading trainer data...</p>
      </div>
    );
  }

  if (trainerError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: 'red' }}>Error loading trainer data. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="add-trainer-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Edit Trainer</h2>
        <PageBreadcrumb
          items={[
            { label: <HomeOutlined />, to: Home },
            { label: 'Trainer Management', to: Home },
            { label: 'Edit Trainer' },
          ]}
        />
      </div>

      <Spin spinning={updating} tip="Updating trainer...">
        <Form
          form={form}
          layout="vertical"
          className="trainer-form"
          onFinish={handleUpdateTrainer}
          onFinishFailed={onFinishFailed as any}
        >
          <div className="row">
            <Form.Item label="Coach Name" name="coachName" rules={[{ required: true, message: 'Please enter coach name' }]}>
              <Input placeholder="Enter coach name" />
            </Form.Item>

            <Form.Item label="Trainer Email" name="trainerEmail" rules={[{ type: 'email', required: true, message: 'Please enter valid email' }]}>
              <Input placeholder="Enter trainer email" />
            </Form.Item>

            <Form.Item label="Coach Phone Number" name="coachPhoneNumber" rules={[{ required: true, message: 'Please enter phone number' }]}>
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item label="Years Of Experience" name="yearsOfExperience" rules={[{ required: true, message: 'Please enter years of experience' }]}>
              <Input type="number" placeholder="Enter years of experience" />
            </Form.Item>

            <Form.Item label="Select Branch" name="branchId" rules={[{ required: true, message: 'Please select at least one branch' }]}>
              <Select mode="multiple" placeholder="Select branch(es)" options={branchOptions} showSearch allowClear />
            </Form.Item>

            <Form.Item label="Role" name="role">
              <Select placeholder="Select" showSearch options={rolesOptions} />
            </Form.Item>

            <Form.Item label="Upload Photo" name="photo">
              <ImagePicker form={form} name="photo" />
            </Form.Item>
          </div>

          <Form.Item
            label="Select Type"
            name="type"
            rules={[{ required: true, message: 'Please select at least one type' }]}
            className="full-width-field"
          >
            <Checkbox.Group>
              <div className="checkbox-grid">
                {TYPE_OPTIONS.map(option => (
                  <Checkbox key={option.value} value={option.value}>{option.label}</Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <div className="row">
            <Form.Item name="idType" label="ID Type">
              <Select placeholder="Select ID Type" options={ID_TYPE_OPTIONS} />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.idType !== cur.idType}>
              {({ getFieldValue }) => {
                const idType: string = getFieldValue('idType');
                if (!idType) return null;

                const idField = (() => {
                  if (idType === 'Passport') {
                    return (
                      <>
                        <Form.Item name="passportNumber" label="Passport Number" rules={[{ required: true, message: 'Please enter passport number' }]}>
                          <Input placeholder="Enter passport number" />
                        </Form.Item>
                        <Form.Item name="nationality" label="Nationality" rules={[{ required: true, message: 'Please enter nationality' }]}>
                          <Input placeholder="Enter nationality" />
                        </Form.Item>
                      </>
                    );
                  }
                  if (idType === 'Aadhar') {
                    return (
                      <Form.Item
                        name="idNumber"
                        label="Aadhar Number"
                        normalize={(v) => (v ? String(v).replace(/\s+/g, '') : v)}
                        rules={[
                          { required: true, message: 'Please enter Aadhar number' },
                          { pattern: /^[0-9]{12}$/, message: 'Aadhar number must be exactly 12 digits' },
                        ]}
                      >
                        <Input placeholder="Enter 12 digit Aadhar number" maxLength={12} />
                      </Form.Item>
                    );
                  }
                  if (idType === 'Driving Licence') {
                    return (
                      <Form.Item
                        name="idNumber"
                        label="Driving Licence Number"
                        normalize={(v) => (v ? String(v).toUpperCase().replace(/\s+/g, '') : v)}
                        rules={[
                          { required: true, message: 'Please enter driving licence number' },
                          { pattern: /^[A-Z]{2}[0-9]{13}$/, message: 'DL format: 2 letters + 13 digits (e.g., MH1420110012345)' },
                        ]}
                      >
                        <Input placeholder="Enter DL number (e.g., MH1420110012345)" maxLength={15} style={{ textTransform: 'uppercase' }} />
                      </Form.Item>
                    );
                  }
                  if (idType === 'pan') {
                    return (
                      <Form.Item
                        name="idNumber"
                        label="PAN Number"
                        normalize={(v) => (v ? String(v).toUpperCase().replace(/\s+/g, '') : v)}
                        rules={[
                          { required: true, message: 'Please enter PAN number' },
                          { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'PAN format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)' },
                        ]}
                      >
                        <Input placeholder="Enter PAN number (e.g., ABCDE1234F)" maxLength={10} style={{ textTransform: 'uppercase' }} />
                      </Form.Item>
                    );
                  }
                  if (idType === 'Voter ID') {
                    return (
                      <Form.Item
                        name="idNumber"
                        label="Voter ID Number"
                        normalize={(v) => (v ? String(v).toUpperCase().replace(/\s+/g, '') : v)}
                        rules={[
                          { required: true, message: 'Please enter voter ID number' },
                          { pattern: /^[A-Z]{3}[0-9]{7}$/, message: 'Voter ID format: 3 letters + 7 digits (e.g., ABC1234567)' },
                        ]}
                      >
                        <Input placeholder="Enter voter ID number (e.g., ABC1234567)" maxLength={10} style={{ textTransform: 'uppercase' }} />
                      </Form.Item>
                    );
                  }
                  return (
                    <Form.Item name="idNumber" label="ID Number">
                      <Input placeholder="Enter ID number" />
                    </Form.Item>
                  );
                })();

                return (
                  <>
                    {idField}
                    <div className="row id-images-row">
                      <Form.Item name="idFront" label="ID Front">
                        <ImagePicker form={form} name="idFront" />
                      </Form.Item>
                      <Form.Item name="idBack" label="ID Back">
                        <ImagePicker form={form} name="idBack" />
                      </Form.Item>
                    </div>
                  </>
                );
              }}
            </Form.Item>
          </div>

          <div className="certificates-section">
            <h3 style={{ color: 'var(--sider-text)', marginBottom: 16 }}>Certificates</h3>
            <Form.List name="certificates">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="certificate-item">
                      <div className="row">
                        <Form.Item
                          {...restField}
                          name={[name, 'title']}
                          label="Certificate Name"
                          rules={[{ required: true, message: 'Please enter certificate name' }]}
                        >
                          <Input placeholder="Enter certificate name" />
                        </Form.Item>
                        <Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} style={{ marginTop: 30 }}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Certificate
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>

          <div className="footer-buttons">
            <Button className="delete-btn" onClick={() => form.resetFields()}>
              <DeleteOutlined /> RESET
            </Button>
            <Button className="save-btn" type="primary" htmlType="submit" loading={updating}>
              UPDATE
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default EditTrainer;
